/**
 * GenAI retry helper — VMR-2 defense-in-depth.
 *
 * Wraps callers of the @google/genai SDK + the /api/gemini-proxy aistudio
 * branch with a small, focused retry loop:
 *
 *   - Exponential backoff (250ms, 500ms, 1000ms by default; capped at 4s).
 *   - Classification of transient vs fatal failures so we never burn
 *     budget retrying a 401, an `invalid` payload, or an AI-Studio
 *     monthly-spend-cap 429 — but we DO retry the transient cases that
 *     historically cause the most user-visible noise (404 on /v1/models
 *     while a Cloud Run rev is rolling, 502/503 from the proxy under
 *     pressure, AbortError from a flaky upstream, plain `fetch failed`).
 *
 * Composition with the VMR P0 hotfix (rev `00493-jlk`):
 *   `getBestModel` no longer hits the network — it returns the first
 *   entry of `filterModelsForBackend(MODEL_PREFERENCES)` directly. This
 *   helper does NOT replace that fix; it wraps callers whose underlying
 *   work IS a network call (model-resolution future-proofing, the
 *   aistudio-proxy path used by the research dispatcher). The P0 path
 *   remains synchronous + deterministic — `withGenAIRetry(syncFn)` is an
 *   identity wrapper when the inner function never throws.
 *
 * Composition with `withRetry` already inside `geminiService.ts`:
 *   That helper is module-private and tightly coupled to
 *   `_resolvedModel` invalidation + the large-scale generate-content
 *   call-sites with their existing telemetry. `withGenAIRetry` exists
 *   for the surfaces OUTSIDE geminiService — the research dispatcher
 *   (separate module), the aistudio-proxy admin probe, and any future
 *   genai surface that needs the same transient/fatal classification.
 *   Both helpers can coexist; tests pin both.
 *
 * Design constraints (from VMR pack §VMR-2):
 *   - Pure function; injectable `now` + `delay` for deterministic tests.
 *   - Default maxAttempts = 3 (the SDK already absorbs one transient
 *     before surfacing; 3 outer attempts catches the rest without
 *     making the user wait through a >10s loop).
 *   - Caller can pass `onTransient` to hook per-retry telemetry (e.g.
 *     emit `[provider:gemini] retry-attempt` in the geminiService
 *     callsite); helper itself emits no console output.
 *   - Hard fail-fast on the explicit fatal classes — no waste retrying
 *     401, 403, 'invalid', or 'monthly spending cap'. The model-cache
 *     invalidation hook lets the caller reset `_resolvedModel` when a
 *     model-not-found error fires so the next attempt re-resolves.
 */

import { abortableDelay, raceWithAbort, rethrowIfAborted, throwIfAborted } from '../lib/abortSignal';

/** Classification of an error caught by `withGenAIRetry`. */
export type GenAIFailureClass =
  | 'transient'        // 502/503/504, fetch failed, AbortError, generic network
  | 'fatal-auth'       // 401, 403, invalid API key
  | 'fatal-quota'      // AI Studio monthly spend cap (terminal)
  | 'fatal-invalid'    // 400-ish "invalid request" — no point retrying
  | 'transient-model'  // 404 / model-not-found — retry AFTER invalidating model cache
  | 'transient-429'    // generic 429 / RESOURCE_EXHAUSTED — retry with backoff
  | 'unknown';         // surface only after backoff exhausted

/**
 * Classify a thrown error. Pure function — exported for tests.
 *
 * The matchers are intentionally narrow + ordered so the most specific
 * pattern wins. Anything we can't classify falls to `'unknown'`, which
 * the caller treats as a transient retryable (one more attempt) so a
 * brand-new error shape isn't silently terminal.
 */
export function classifyGenAIError(err: unknown): GenAIFailureClass {
  if (err && typeof err === 'object'
    && (err as { code?: unknown }).code === 'GEMINI_INCOMPLETE_RESPONSE') {
    return 'fatal-invalid';
  }
  const raw = err instanceof Error ? err.message : String(err ?? '');
  const lower = raw.toLowerCase();

  // ── Fatal classes — never retry. ───────────────────────────────────
  // AI Studio's monthly spend cap is terminal: retries cost money and
  // never succeed. Mirrors `classifyQuotaError` in geminiService.ts.
  if (lower.includes('monthly spending cap') || lower.includes('spending cap')) {
    return 'fatal-quota';
  }
  if (lower.includes('401') || lower.includes('unauthorized') || lower.includes('unauthenticated')) {
    return 'fatal-auth';
  }
  if (lower.includes('403') || lower.includes('permission_denied') || lower.includes('forbidden')) {
    return 'fatal-auth';
  }
  if (lower.includes('invalid api key') || lower.includes('api_key_invalid')) {
    return 'fatal-auth';
  }
  // `invalid_argument` / `invalid request` — caller's payload is malformed.
  // Distinct from a plain "invalid response" which can be transient.
  if (lower.includes('invalid_argument') || lower.includes('invalid request')) {
    return 'fatal-invalid';
  }

  // ── Transient — retry. ─────────────────────────────────────────────
  // Model-not-found: retry AFTER cache invalidation hook fires. The
  // caller's `onModelMiss` resets `_resolvedModel` so the next attempt
  // re-resolves against the preference list.
  if (
    lower.includes('not found') ||
    lower.includes('404') ||
    lower.includes('no longer available') ||
    lower.includes('model not found')
  ) {
    return 'transient-model';
  }

  // 429: generic burst — retry with backoff. The AI-Studio cap case is
  // already caught above as `'fatal-quota'`; everything else here is
  // worth retrying once or twice.
  if (lower.includes('429') || lower.includes('resource_exhausted') || lower.includes('quota exceeded')) {
    return 'transient-429';
  }

  // 502 / 503 / 504 / abort / fetch failed / timed out.
  if (
    lower.includes('502') ||
    lower.includes('503') ||
    lower.includes('504') ||
    lower.includes('high demand') ||
    lower.includes('unavailable') ||
    lower.includes('fetch failed') ||
    lower.includes('aborterror') ||
    lower.includes('abort error') ||
    lower.includes('timed out')
  ) {
    return 'transient';
  }

  return 'unknown';
}

/** Options accepted by `withGenAIRetry`. */
export interface GenAIRetryOptions {
  /** Cancels the active transport/backoff and forbids any later attempt. */
  signal?: AbortSignal;
  /** Label used by tests / callers to disambiguate telemetry. Optional. */
  label?: string;
  /** Max retry attempts. Default 3 (covers two short-lived transients). */
  maxAttempts?: number;
  /** Base backoff in ms. Doubles each attempt; capped at `maxBackoffMs`. */
  baseDelayMs?: number;
  /** Cap on the exponential backoff. Default 4000ms. */
  maxBackoffMs?: number;
  /**
   * Caller veto on retrying an error the classifier would retry. Consulted
   * after the fatal check; returning false surfaces the error at once, like a
   * fatal class (no `onModelMiss`, no `onTransient`, no backoff). Unset keeps
   * the classifier's decision. DM2 doc matching uses it to stop re-firing a
   * `/api/gemini-proxy` 429 into the same closed 60 s IP bucket — its own
   * throttle waits out the `Retry-After` instead.
   */
  shouldRetry?: (error: unknown, classification: GenAIFailureClass) => boolean;
  /**
   * Invoked on `'transient-model'` so the caller can invalidate any
   * resolved-model cache. Returning a promise is honoured (awaited)
   * before the retry. Optional — model-resolution callers wire this;
   * pure-network callers leave it unset.
   */
  onModelMiss?: () => void | Promise<void>;
  /**
   * Per-retry telemetry hook. Fires AFTER classification, BEFORE the
   * backoff sleep. Receives the attempt index (1-based) and the
   * classification so the caller can emit a `[provider:gemini]
   * retry-attempt` metric. Optional.
   */
  onTransient?: (info: {
    attempt: number;
    classification: GenAIFailureClass;
    error: unknown;
    label?: string;
  }) => void;
  /**
   * Injectable timer — tests stub this to advance instantly. Default
   * uses real `setTimeout`.
   */
  delay?: (ms: number) => Promise<void>;
}

/** Compute the backoff for a given 1-based attempt index. */
export function computeBackoffMs(
  attempt: number,
  baseDelayMs: number,
  maxBackoffMs: number,
): number {
  // attempt=1 → base, attempt=2 → 2x base, attempt=3 → 4x base, capped.
  const raw = baseDelayMs * 2 ** Math.max(0, attempt - 1);
  return Math.min(raw, maxBackoffMs);
}

/**
 * Run `fn` with exponential backoff + transient/fatal classification.
 *
 * Hard contract:
 *  - Returns the resolved value on first success.
 *  - On a `'fatal-*'` classification, throws immediately — no retries.
 *  - When `shouldRetry` returns false, throws immediately — no retries.
 *  - On a `'transient-*'` or `'unknown'` classification, sleeps for the
 *    computed backoff and retries up to `maxAttempts` total tries.
 *  - On final exhaustion, throws the most recent error (caller sees the
 *    same shape as if no retry helper were in place — backward-compat).
 *
 * The helper does NOT mutate the error message or wrap it; the caller
 * sees the original failure surface. This matches the VMR pack's
 * regression-invariant #1: surface text the user (or upstream logger)
 * sees must not drift.
 */
export async function withGenAIRetry<T>(
  fn: () => Promise<T>,
  opts: GenAIRetryOptions = {},
): Promise<T> {
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 3);
  const baseDelayMs = Math.max(0, opts.baseDelayMs ?? 250);
  const maxBackoffMs = Math.max(baseDelayMs, opts.maxBackoffMs ?? 4_000);

  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    throwIfAborted(opts.signal);
    try {
      const result = await fn();
      throwIfAborted(opts.signal);
      return result;
    } catch (err) {
      rethrowIfAborted(opts.signal, err);
      lastErr = err;
      const klass = classifyGenAIError(err);

      // Fatal — never retry. Surface the original error verbatim.
      if (klass === 'fatal-auth' || klass === 'fatal-quota' || klass === 'fatal-invalid') {
        throw err;
      }
      // Caller veto — same fail-fast surface as a fatal class.
      if (opts.shouldRetry && !opts.shouldRetry(err, klass)) {
        throw err;
      }

      // Invalidate model cache on model-miss BEFORE the retry sleep so
      // the next attempt re-resolves. Callers wire this for getBestModel
      // call-sites; for pure-network callers it's a no-op.
      if (klass === 'transient-model' && opts.onModelMiss) {
        try {
          await opts.onModelMiss();
          throwIfAborted(opts.signal);
        } catch (hookError) {
          rethrowIfAborted(opts.signal, hookError);
          // Hook failure is non-fatal — the next attempt will surface
          // any deeper issue via the inner fn.
        }
      }

      opts.onTransient?.({ attempt, classification: klass, error: err, label: opts.label });

      // If this was the last attempt, stop here and surface the error.
      if (attempt >= maxAttempts) break;

      const backoff = computeBackoffMs(attempt, baseDelayMs, maxBackoffMs);
      if (backoff > 0) {
        if (opts.delay) await raceWithAbort(opts.delay(backoff), opts.signal);
        else await abortableDelay(backoff, opts.signal);
      }
    }
  }
  // All retries exhausted — surface the most recent error.
  throw lastErr;
}
